import { isCrmHubEnabled } from "../../utils/crmHubFlag";
import { AGENT_FEATURE_KEYS } from "./constants";

function _normalizePublicTierCode(value) {
  const u = String(value || "").trim().toUpperCase();
  if (!u) return "";
  if (u === "STARTER" || u === "BASIC") return "BASIC";
  if (u === "PRO") return "PRO";
  if (u === "AGENCY") return "AGENCY";
  return "";
}

/**
 * אדמין / super_admin עם tier PRO אפקטיבי מ־API — פותח את כל גייטי ה-boolean כמו PRO מלא,
 * גם כשמפתחות חסרים ב-features (SSOT + overlay).
 */
function _adminEffectiveProTierUxUnlocked(payload, adminEffectivePro) {
  const tier = _normalizePublicTierCode(
    payload?.effectivePackageCode ??
      (payload?.package && typeof payload.package === "object"
        ? payload.package.code
        : "")
  );
  if (tier !== "PRO") return false;
  const overlay = Boolean(payload?.entitlementsAudit?.adminProEntitlementsOverlay);
  return overlay || Boolean(adminEffectivePro);
}

/**
 * @param {Record<string, unknown>|null|undefined} features
 * @param {string} key
 * @param {{ whenUnknown?: boolean }} [opts] whenUnknown: value if not loaded / key missing — ברירת מחדל false (ללא fail-open)
 */
export function featureBool(features, key, opts = {}) {
  const { whenUnknown = false } = opts;
  if (!features || typeof features !== "object") return whenUnknown;
  if (!Object.prototype.hasOwnProperty.call(features, key)) return whenUnknown;
  const v = features[key];
  if (v === null || v === undefined) return whenUnknown;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "" || s === "0" || s === "false" || s === "no") return false;
    if (s === "true" || s === "yes" || s === "1") return true;
    return Boolean(v);
  }
  return Boolean(v);
}

/**
 * @param {Record<string, unknown>|null|undefined} features
 * @param {string} key
 * @returns {number|null}
 */
export function featureNumber(features, key) {
  if (!features || typeof features !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(features, key)) return null;
  const v = features[key];
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Flight search (PLANES tab / mockup) — same semantics as hotels via featureBool.
 * @param {Record<string, unknown>|null|undefined} features
 * @param {{ whenUnknown?: boolean }} [opts]
 */
export function canSearchFlights(features, opts = {}) {
  return featureBool(features, AGENT_FEATURE_KEYS.SEARCH_FLIGHTS, opts);
}

export function canUseOrganizedTours(features, opts = {}) {
  return featureBool(features, AGENT_FEATURE_KEYS.ORGANIZED_TOURS, opts);
}

/**
 * Package matrix: hotel-only HR commission % for agent hotel search results.
 * Missing / invalid → 0 (no markup).
 * @param {object|null|undefined} payload
 * @returns {number}
 */
export function resolveHotelHrCommissionPercentageForHotelResults(payload) {
  const n = featureNumber(payload?.features, AGENT_FEATURE_KEYS.HOTEL_HR_COMMISSION_PERCENTAGE);
  if (n == null || !Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * אחוז פיצול דמי טיפול (handling) סוכן/חברה — ארנק / wallet בלבד (`commission_percentage`).
 * אסור לתמחור LDS או ל-unifiedHrMarginPercent.
 * @param {object|null|undefined} payload
 * @returns {number|null}
 */
export function resolveEffectiveCommissionPercentage(payload) {
  const eff = payload?.effectiveCommissionPercentage;
  if (eff !== null && eff !== undefined && eff !== "") {
    const n = Number(eff);
    if (Number.isFinite(n)) return n;
  }
  return featureNumber(payload?.features, AGENT_FEATURE_KEYS.COMMISSION_PERCENTAGE);
}

/** Alias מפורש ל-wallet — אותה פונקציה כמו resolveEffectiveCommissionPercentage */
export const resolveWalletCommissionPercentage = resolveEffectiveCommissionPercentage;

/**
 * @param {{ dealType?: string, deal_type?: string, tour?: unknown, hotel?: unknown, isOrganizedTour?: boolean, isHotelDeal?: boolean, enforceFlightHrCommissionSsot?: boolean }} [ctx]
 * @returns {'vacation_package'|'flight'|'hotel'|'organized_tour'}
 */
export function inferLdsHrProductType(ctx = {}) {
  const dtRaw = ctx.dealType ?? ctx.deal_type ?? "";
  const cdt = String(dtRaw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s_/&+-]+/g, "");

  if (cdt.includes("ORGANIZEDTOUR") || cdt === "ORGANIZEDTOURS" || cdt === "ORGANIZED") {
    return "organized_tour";
  }
  if (Boolean(ctx.isOrganizedTour)) return "organized_tour";

  const tour = ctx.tour;
  const hotel = ctx.hotel;
  const tourPresent =
    tour && typeof tour === "object" && Object.keys(tour).length > 0;
  const hotelPresent =
    Boolean(hotel) &&
    !(typeof hotel === "object" && hotel != null && Object.keys(hotel).length === 0);
  if (tourPresent && !hotelPresent) return "organized_tour";

  if (Boolean(ctx.isHotelDeal)) return "hotel";
  if (Boolean(ctx.enforceFlightHrCommissionSsot)) return "flight";
  return "vacation_package";
}

/**
 * מרווח HR לתמחור LDS לפי פיצ'רי חבילה (לא commission_percentage).
 * @param {Record<string, unknown>|null|undefined} features
 * @param {'vacation_package'|'flight'|'hotel'|'organized_tour'} productType
 * @returns {{ percent: number|null, sourceFeatureKey: string|null }}
 */
export function resolveLdsHrMarginFromFeatures(features, productType) {
  const pt = String(productType || "").trim().toLowerCase();
  const hotelKey = AGENT_FEATURE_KEYS.HOTEL_HR_COMMISSION_PERCENTAGE;

  const tryPrimaryThenHotel = (primaryKey) => {
    const p = featureNumber(features, primaryKey);
    if (p != null && Number.isFinite(p) && p >= 0) {
      return { percent: p, sourceFeatureKey: primaryKey };
    }
    const h = featureNumber(features, hotelKey);
    if (h != null && Number.isFinite(h) && h >= 0) {
      return { percent: h, sourceFeatureKey: hotelKey };
    }
    return { percent: null, sourceFeatureKey: null };
  };

  if (pt === "organized_tour") {
    const ot = featureNumber(features, AGENT_FEATURE_KEYS.ORGANIZED_TOUR_HR_COMMISSION_PERCENTAGE);
    if (ot != null && Number.isFinite(ot) && ot >= 0) {
      return {
        percent: ot,
        sourceFeatureKey: AGENT_FEATURE_KEYS.ORGANIZED_TOUR_HR_COMMISSION_PERCENTAGE,
      };
    }
    const vpKey = AGENT_FEATURE_KEYS.VACATION_PACKAGE_HR_COMMISSION_PERCENTAGE;
    const vp = featureNumber(features, vpKey);
    if (vp != null && Number.isFinite(vp) && vp >= 0) {
      return { percent: vp, sourceFeatureKey: vpKey };
    }
    const hOnly = featureNumber(features, hotelKey);
    if (hOnly != null && Number.isFinite(hOnly) && hOnly >= 0) {
      return { percent: hOnly, sourceFeatureKey: hotelKey };
    }
    return { percent: null, sourceFeatureKey: null };
  }

  if (pt === "vacation_package") {
    return tryPrimaryThenHotel(
      AGENT_FEATURE_KEYS.VACATION_PACKAGE_HR_COMMISSION_PERCENTAGE
    );
  }
  if (pt === "flight") {
    return tryPrimaryThenHotel(AGENT_FEATURE_KEYS.FLIGHT_HR_COMMISSION_PERCENTAGE);
  }
  const hOnly = featureNumber(features, hotelKey);
  if (hOnly != null && Number.isFinite(hOnly) && hOnly >= 0) {
    return { percent: hOnly, sourceFeatureKey: hotelKey };
  }
  return { percent: null, sourceFeatureKey: null };
}

/**
 * @param {object|null|undefined} payload — full /api/agents/me/entitlements response
 * @param {{ status?: string, adminEffectivePro?: boolean }} [meta]
 *   adminEffectivePro + status לא ok: הרפיית boolean בזמן טעינה/שגיאת API (אדמין בלבד)
 */
export function buildEntitlementSelectors(payload, meta = {}) {
  const { status, adminEffectivePro = false } = meta || {};
  const adminGateRelax =
    Boolean(adminEffectivePro) &&
    (status === "idle" || status === "loading" || status === "error");
  const whenUnknown = adminGateRelax;
  const features = payload?.features;
  const adminProActions = _adminEffectiveProTierUxUnlocked(
    payload,
    adminEffectivePro
  );
  const ap = adminProActions;
  const fb = (matrixKey) =>
    featureBool(features, matrixKey, { whenUnknown }) || ap;

  return {
    effectivePackageCode:
      payload?.effectivePackageCode ??
      (payload?.package && typeof payload.package === "object"
        ? payload.package.code
        : null),
    canSearchHotels: fb(AGENT_FEATURE_KEYS.SEARCH_HOTELS),
    canSearchPackages: fb(AGENT_FEATURE_KEYS.SEARCH_PACKAGES),
    canSearchFlights: canSearchFlights(features, { whenUnknown }) || ap,
    canUseDynamicPackages: fb(AGENT_FEATURE_KEYS.DYNAMIC_PACKAGES),
    canOrganizedTours: canUseOrganizedTours(features, { whenUnknown }) || ap,
    hasPrioritySupport: fb(AGENT_FEATURE_KEYS.PRIORITY_SUPPORT),
    // CRM Hub phase: allow BASIC+ without waiting for package_feature_values SQL.
    // Matrix still drives upgrade UX elsewhere; hub APIs use session auth on BE.
    canUseLeadsSystem:
      fb(AGENT_FEATURE_KEYS.LEADS_SYSTEM) || isCrmHubEnabled({
        effectivePackageCode:
          payload?.effectivePackageCode ??
          (payload?.package && typeof payload.package === "object"
            ? payload.package.code
            : null),
      }),
    canUseSeoSystem: fb(AGENT_FEATURE_KEYS.SEO_SYSTEM),
    canUseSmartQuotes: fb(AGENT_FEATURE_KEYS.SMART_QUOTES),
    /** AI Sales + צ'אט לקוחות — מפתח בשרת crazy_llama */
    canUseAiSales: fb(AGENT_FEATURE_KEYS.AI_SALES),
    canUseCrazyLlama: fb(AGENT_FEATURE_KEYS.CRAZY_LLAMA),
    canUseHotelPrebooking: fb(AGENT_FEATURE_KEYS.HOTEL_PREBOOKING),
    canUseTeamManagement: fb(AGENT_FEATURE_KEYS.TEAM_MANAGEMENT),
    canUseReferralRewards: fb(AGENT_FEATURE_KEYS.REFERRAL_REWARDS),
    /** Package matrix value only — unchanged by agent personal override */
    commissionPercentage: featureNumber(features, AGENT_FEATURE_KEYS.COMMISSION_PERCENTAGE),
    /** Hotel search results markup only; not effectiveCommission override */
    hotelHrCommissionPercentage: resolveHotelHrCommissionPercentageForHotelResults(payload),
    /** Wallet / handling split only — לא HR margin */
    effectiveCommissionPercentage: resolveEffectiveCommissionPercentage(payload),
    maxMonthlyOrders: featureNumber(features, AGENT_FEATURE_KEYS.MAX_MONTHLY_ORDERS),
    canUseMonthlyPricing: fb(AGENT_FEATURE_KEYS.MONTHLY_PRICING),
    canUseCustomerOfferLink: fb(AGENT_FEATURE_KEYS.CUSTOMER_OFFER_LINK),
    canUseMultiPackageCustomerOffer: fb(
      AGENT_FEATURE_KEYS.MULTI_PACKAGE_CUSTOMER_OFFER
    ),
    /** סיכום חיוב / פירוט פיננסי — alias: canViewBillingSummary */
    canViewBillingSummary: fb(AGENT_FEATURE_KEYS.BILLING_ACCESS),
    canUseAgentPublicSite: fb(AGENT_FEATURE_KEYS.AGENT_PUBLIC_SITE),
    canUseManoQuotes: fb(AGENT_FEATURE_KEYS.MANO_QUOTES),
  };
}
